import { Component, computed, DestroyRef, inject, Signal, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AbstractControl, FormControl, FormGroup, ValidationErrors, ValidatorFn } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef, MatDialogActions, MatDialogContent } from '@angular/material/dialog';
import { PhButtonComponent } from '../../../core/components/ph-button/ph-button.component';
import { PhIconComponent } from '../../../core/components/ph-icon/ph-icon.component';
import { IPhModalAnswer } from '../../../core/modals/ph-modal/models/phModalAnswer';
import { RequiredFieldPipe } from '../../../core/pipes/required-field.pipe';
import { SHARED_MODULES } from '../../../shared.';

export type IControlCancelSendModalData = {
	count: number;
	foreignCount: number;
	uniquePlansCount: number;
	scheduledOnly: boolean;
	unscheduledOnly: boolean;
};

@Component({
	selector: 'app-control-cancel-send-modal',
	templateUrl: './control-cancel-send-modal.component.html',
	styleUrls: ['./control-cancel-send-modal.component.scss'],
	imports: [
		SHARED_MODULES,
		PhIconComponent,
		PhButtonComponent,
		MatDialogActions,
		MatDialogContent,
		RequiredFieldPipe,
	],
})
export class ControlCancelSendModalComponent {

	private readonly data = signal(inject<IControlCancelSendModalData>(MAT_DIALOG_DATA));

	protected readonly count: Signal<number> = computed(() => this.data().count);
	protected readonly foreignCount: Signal<number> = computed(() => this.data().foreignCount);
	protected readonly uniquePlansCount: Signal<number> = computed(() => this.data().uniquePlansCount);
	protected readonly unscheduledOnly: Signal<boolean> = computed(() => this.data().unscheduledOnly);
	protected readonly showForeignWarning: Signal<boolean> = computed(() => this.foreignCount() > 0);
	protected readonly showShopsConfirm: Signal<boolean> = computed(() => this.showForeignWarning());
	protected readonly shopsFormSubmitted = signal(false);
	protected readonly shopsFormGroup = new FormGroup({
		shopsCount: new FormControl<string>('', {
			nonNullable: true,
			validators: [ControlCancelSendModalComponent.shopsCountValidator(this.uniquePlansCount())],
		}),
	});
	protected isConfirmDisabled(): boolean {
		return this.showShopsConfirm() && this.shopsFormSubmitted() && this.shopsFormGroup.invalid;
	}
	protected readonly titleKey: Signal<string> = computed(() =>
		this.data().scheduledOnly
			? 'CONTROL_DASHBOARD.CANCEL_SEND_SCHEDULED_TITLE'
			: 'CONTROL_DASHBOARD.CANCEL_SEND_TITLE'
	);
	protected readonly foreignWarningKey: Signal<string> = computed(() => {
		const { scheduledOnly, unscheduledOnly, count } = this.data();
		if (scheduledOnly) {
			return count === 1
				? 'CONTROL_DASHBOARD.CANCEL_SEND_OTHER_SCHEDULED_ONE'
				: 'CONTROL_DASHBOARD.CANCEL_SEND_OTHER_SCHEDULED_MANY';
		}
		if (unscheduledOnly) {
			return count === 1
				? 'CONTROL_DASHBOARD.CANCEL_SEND_OTHER_SENT_ONE'
				: 'CONTROL_DASHBOARD.CANCEL_SEND_OTHER_SENT_MANY';
		}
		return 'CONTROL_DASHBOARD.CANCEL_SEND_OTHER_MIXED';
	});
	protected readonly confirmButtonKey: Signal<string> = computed(() =>
		this.count() === 1
			? 'CONTROL_DASHBOARD.CANCEL_SEND_BUTTON_ONE'
			: 'CONTROL_DASHBOARD.CANCEL_SEND_BUTTON'
	);

	private readonly dialogRef: MatDialogRef<ControlCancelSendModalComponent, IPhModalAnswer> = inject(
		MatDialogRef<ControlCancelSendModalComponent, IPhModalAnswer>
	);
	private readonly destroyRef: DestroyRef = inject(DestroyRef);

	constructor() {
		this.dialogRef
			.beforeClosed()
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe((result: IPhModalAnswer | undefined) => {
				if (result === undefined) this.close();
			});
	}

	private static shopsCountValidator(expected: number): ValidatorFn {
		return (control: AbstractControl<string>): ValidationErrors | null => {
			const parsed = Number(String(control.value ?? '').trim());
			return Number.isInteger(parsed) && parsed === expected ? null : { shopsCountMismatch: true };
		};
	}

	protected confirm(): void {
		if (this.showShopsConfirm()) {
			this.shopsFormSubmitted.set(true);
			if (this.shopsFormGroup.invalid) return;
		}
		this.dialogRef.close({ buttonId: 'confirm' });
	}

	protected close(): void {
		this.dialogRef.close({ buttonId: null });
	}
}
